/* eslint-disable @typescript-eslint/no-explicit-any */
import { useStore } from '../store'

const API_BASE_URL = import.meta.env.VITE_API_URL

interface RequestOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>
}

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  status: number
  data: any

  constructor(message: string, status: number, data: any) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

/**
 * Base wrapper for fetch requests
 */

let onUnauthorizedCallback: (() => Promise<string | null>) | null = null

export function registerUnauthorizedHandler(handler: () => Promise<string | null>) {
  onUnauthorizedCallback = handler
}

/**
 * Base wrapper for fetch requests
 */
export async function request<T>(endpoint: string, options: RequestOptions = {}, retries = 1): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`
  
  const headers: Record<string, string> = {
    // Default headers
    'Content-Type': 'application/json',
    ...options.headers,
  }

  // Inject Access Token if available
  const accessToken = useStore.getState().user.accessToken
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }



  // Handle FormData: Content-Type should be let undefined so browser sets it with boundary
  if (options.body instanceof FormData) {
    delete (headers as any)['Content-Type']
  }

  const response = await fetch(url, {
    ...options,
    headers,
  })

  // Handle 401 Unauthorized with Retry
  if (response.status === 401 && retries > 0 && onUnauthorizedCallback) {
    console.warn('Received 401, attempting token refresh...')
    try {
      // Attempt to refresh token
      const newToken = await onUnauthorizedCallback()
      
      if (newToken) {
        // Update store synchronously just in case (though handler usually does it)
        useStore.getState().setAccessToken(newToken)
        
        // Retry request with decremented retry count
        return request<T>(endpoint, options, retries - 1)
      }
    } catch (err) {
      console.error('Token refresh failed:', err)
      // Fall through to error handling
    }
  }

  // Parse JSON response if possible, otherwise return null or text
  let data: any
  const contentType = response.headers.get('content-type')
  if (contentType && contentType.includes('application/json')) {
    data = await response.json().catch(() => ({}))
  } else {
    data = await response.text().catch(() => null)
  }

  if (!response.ok) {
    const errorMessage = 
      data?.message || 
      data?.error || 
      (data?.error_code ? `Error ${data.error_code}` : null) ||
      data?.detail || 
      `Request failed with status ${response.status}`

    // Attach request_id if available
    if (data?.request_id) {
       // We could append it to message or just attach it to the error object instance if needed
       // For now, let's leave it in `data` which is passed to ApiError
    }

    throw new ApiError(
      errorMessage,
      response.status,
      data
    )
  }

  return data as T
}

/**
 * Typed helper methods (optional, but convenient)
 */
export const api = {
  get: <T>(endpoint: string, options?: RequestOptions) => request<T>(endpoint, { ...options, method: 'GET' }),
  post: <T>(endpoint: string, body: any, options?: RequestOptions) => {
    // Check if body is FormData, if so pass it directly
    const isFormData = body instanceof FormData
    return request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: isFormData ? body : JSON.stringify(body),
    })
  },
  put: <T>(endpoint: string, body: any, options?: RequestOptions) => 
    request<T>(endpoint, { ...options, method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(endpoint: string, options?: RequestOptions) => 
    request<T>(endpoint, { ...options, method: 'DELETE' }),
}
